import React, { useEffect, useState } from "react";
import { BuyGiftCardSubpageEnum } from '../../../src/models/enums/BuyGiftCardSubpageEnum';
import { isVideoFile } from "../../helpers/Utils";
import { ICheckBalanceStep } from "../../models/CheckBalanceStep.model";
import { AppSettingContext } from "../Application";
import LoadingComponent from "../shared/LoadingComponent";
import BuyGiftCardMailTemplate from "./BuyGiftCardMailTemplate";

interface IGiftCardDetails {
    isSaving: boolean;
    subpage?: BuyGiftCardSubpageEnum,
    cardValue: number;

    giftCardUrl: string;
    initialTemplateUrl: string;
    previewTemplateUrl: string;
    previewMediaUrl: string;
    previewPersonalMessage: string;

    regiftData?: ICheckBalanceStep;
}

const GiftCardDetails = (props: IGiftCardDetails) => {
    const { labels, walletSettings } = React.useContext(AppSettingContext);

    const isVideo = isVideoFile(props.previewMediaUrl);
    const defaultPersonalMessageText = labels.get("GiftCard.App.Steps.Customise.PersonalMessage.DefaultText");
    const personalMessage = props.previewPersonalMessage === defaultPersonalMessageText ? "" : props.previewPersonalMessage;

    //#region STATE PROPERTIES

    const [animationStarted, setAnimationStarted] = useState(false);
    const [templateUrl, setTemplateUrl] = useState(props.previewTemplateUrl ? props.previewTemplateUrl : props.initialTemplateUrl);
    const [isLoading, setIsLoading] = useState(true);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (props.previewTemplateUrl.trim()) {
            if (!animationStarted) {
                setAnimationStarted(true);
                setTimeout(() => {
                    setTemplateUrl(props.previewTemplateUrl);
                }, 200);
            }
            else {
                setIsLoading(true);
                setTimeout(() => {
                    setTemplateUrl(props.previewTemplateUrl);
                    setIsLoading(false);
                }, 200);
            }
        }
    }, [props.previewTemplateUrl]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    //#endregion

    //#region COMMON HELPERS

    const encodeHTML = (input: string): string => {
        return input.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    //#endregion

    return (
        <>
            {!props.subpage && (
                <label htmlFor="mobile-card-preview" style={{ zIndex: '0' }}>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 29L29 3M3 3L29 29" stroke="#111827" strokeWidth="2.67" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </label>
            )}

            <div className="my-card box">
                <div className="box-heading show-edit-icon">
                    <h3>{labels.get("GiftCard.App.V2.Sidebar.MyCard.Title")}</h3>
                </div>
                <div className="card-new">
                    <div className={animationStarted ? "card-animation animate" : "card-animation"}>
                        {templateUrl && (
                            <div>
                                <img src={templateUrl} style={{ borderRadius: '24px' }} alt="Options Card" />

                                {props.regiftData ? (
                                    <div className="mycard-info-wrapper">
                                        <span className="card-new__info-total">
                                            {`${props.regiftData.currencySymbol}${props.regiftData.remainingBalance.toFixed(2)}`}
                                        </span>
                                        <span className="card-new__info-total">
                                            {props.regiftData.redeemCode}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="card-new__info" style={{ display: props.cardValue ? "flex" : "none" }}>
                                        <span className="card-new__info-total">&euro;{props.cardValue.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {(props.regiftData && props.regiftData.redeemCode) && (
                        <div className="wallets" style={{ marginTop: '0' }}>
                            {walletSettings.appleWalletUrl && (
                                <a href={walletSettings.appleWalletUrl.replace("{UVCode}", props.regiftData.redeemCode)}>
                                    <img src="/static/img/apple-wallet.png" alt="Apple Wallet" />
                                </a>
                            )}

                            {walletSettings.googleWalletUrl && (
                                <a href={walletSettings.googleWalletUrl.replace("{UVCode}", props.regiftData.redeemCode)}>
                                    <img src="/static/img/google-wallet.png" alt="Google Wallet" />
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <LoadingComponent />
                ) : (
                    <>
                        {!props.subpage && (personalMessage.trim() || props.previewMediaUrl.trim()) && (
                            <div className="preview_box">
                                <div className="flex-container">
                                    {personalMessage.trim() && (
                                        <p
                                            className="text small-fs text-wrap"
                                            style={{ overflowWrap: "anywhere" }}
                                            dangerouslySetInnerHTML={{ __html: encodeHTML(personalMessage) }}
                                        ></p>
                                    )}

                                    {props.previewMediaUrl.trim() && (
                                        <div className="unwrap-gift">
                                            <div className="unwrap-gift--box" style={{ width: '100%' }} >
                                                {(isVideo) ? (
                                                    <video
                                                        autoPlay
                                                        src={props.previewMediaUrl}
                                                        style={{ width: '100%' }}
                                                    ></video>
                                                ) : (
                                                    <img
                                                        src={props.previewMediaUrl}
                                                        alt="Mail media"
                                                        style={{ width: '100%' }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {props.subpage && props.subpage !== BuyGiftCardSubpageEnum.CHOOSE_CARD && (
                    <BuyGiftCardMailTemplate
                        subpage={props.subpage}
                        personalMessage={personalMessage}
                        mediaUrl={props.previewMediaUrl}
                    />
                )}

                {!props.subpage &&
                    <h5 className="card-new__sub-title">
                        {labels.get("GiftCard.App.V2.Sidebar.MyCard.BottomText")}
                    </h5>
                }
            </div >
        </>
    );
};

export default GiftCardDetails;